import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { useRef } from 'react';

/**
 * Safety net for the dismiss promise. `animateToPositionCompleted` in @gorhom/bottom-sheet
 * bails on interrupted animations, so the completion callback can legitimately never fire.
 * A hung promise would mean no navigation at all, so resolve slightly after the 250ms
 * dismiss animation configured in the shared BottomSheet.
 */
const DISMISS_TIMEOUT_MS = 400;

const usePayOverTimeApplyNowSheet = () => {
  const bottomSheetRef = useRef<BottomSheetModal>(null);
  const isOpenRef = useRef(false);
  const resolveDismissRef = useRef<(() => void) | null>(null);

  const present = () => {
    isOpenRef.current = true;
    bottomSheetRef.current?.present();
  };

  const handleIsOpenChange = (isOpen: boolean) => {
    isOpenRef.current = isOpen;

    if (isOpen) {
      return;
    }

    resolveDismissRef.current?.();
    resolveDismissRef.current = null;
  };

  const dismiss = () =>
    new Promise<void>((resolve) => {
      if (!isOpenRef.current) {
        resolve();
        return;
      }

      resolveDismissRef.current = resolve;
      bottomSheetRef.current?.dismiss();
      setTimeout(() => handleIsOpenChange(false), DISMISS_TIMEOUT_MS);
    });

  return {
    bottomSheetRef,
    present,
    dismiss,
    onIsOpenChange: handleIsOpenChange,
  };
};

export default usePayOverTimeApplyNowSheet;
